import React from 'react'
import { Remark } from 'react-remark'

import EstimateDet from '../interfaces/EstimateDet'
import {
  ClientUser,
  CompleteMark,
  formatCurrency,
  formatDateToDDMMYYYY,
  formatIsoToDDMMYYYY,
  isExpired,
  normalizeDate,
} from '../utils/utils'
import { formatDocumentTitle } from '../utils/documentTitleUtils'
import DocToHeader from '../interfaces/DocInactive'
import { DocStateSpecial } from '../enums/DocStates'

import DocNotes from './DocNotes'
import { OrganizationFrame, OrganizationFrameType } from './OrganizationFrame'
import { ClientFrame, ClientFrameType } from './ClientFrame'
import { VehicleFrame, VehicleFrameType } from './VehicleFrame'
import { DocHeader, DocHeaderType } from './DocHeader'
import { DocStatusHeader } from './DocStatusHeader'

const EstimateDetail: React.FC<{ estimate: EstimateDet | null }> = ({ estimate }) => {
  const salePrice_format = formatCurrency(estimate?.salePrice ?? 0)
  const myNumber = estimate?.number === 0 ? 'XX' : estimate?.number
  const discount = estimate?.discount ?? 0
  const discount_format = discount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const isAnulada = !!estimate?.voidedAt

  // Lo siguiente es porque a veces esta fecha viene "completa" (yyyy-mm-ddTHH etc) (cuando lee desde la base)
  // y a veces no (cuando se está recien creando)
  const aux1 = estimate?.expiresAt ? new Date(normalizeDate(estimate?.expiresAt)) : new Date()

  const isExpirada = !isAnulada && isExpired(aux1)
  const footerExpira = `Esta cotización expira el ${formatDateToDDMMYYYY(aux1)}.`

  const profile_name = estimate?.profile_name ?? ''

  const user_phone = estimate?.user_phone ?? ''
  const user = { name: profile_name, phone: user_phone }

  const organization: OrganizationFrameType = {
    nin: estimate?.organization?.nin ?? '',
    phone: estimate?.organization?.phone ?? '',
    email: estimate?.organization?.email ?? '',
    domain: estimate?.organization?.domain ?? '',
    address: estimate?.organization?.address ?? '',
    name: estimate?.organization?.name ?? '',
    business: estimate?.organization.business,
    user: user,
  }
  const clientHeader: ClientFrameType = {
    nin: estimate?.client.nin,
    name: estimate?.client.name,
    phone: estimate?.client.phone,
    email: estimate?.client.email,
    address: estimate?.client.address,
  }
  const vehicleHeader: VehicleFrameType = {
    plate: estimate?.vehicle.plate,
    type: estimate?.vehicle.type,
    brand: estimate?.vehicle.brand,
    model: estimate?.vehicle.model,
    version: estimate?.vehicle.version,
    year: estimate?.vehicle.year,
    color: estimate?.vehicle.color,
    odometer: estimate?.vehicle.odometer,
    chassisId: estimate?.vehicle.chassisId,
    engineId: estimate?.vehicle.engineId,
    price: estimate?.vehicle.price,
  }
  // Format title using template configuration
  const defaultTitle = 'Cotización'
  const formattedTitle = formatDocumentTitle(estimate?.titleConfig, defaultTitle, myNumber)

  const docHeader: DocHeaderType = {
    urlImage: estimate?.organization.pictures.logotype ?? '',
    titulo: formattedTitle,
    isAnulada: !!estimate?.voidedAt,
    tituloAnulada: 'NULA',
    fechaIniText: `Fecha creación`,
    fechaIniValue: formatIsoToDDMMYYYY(normalizeDate(estimate?.date)),
    fechaFinText: 'Fecha expiración',
    fechaFinValue: formatIsoToDDMMYYYY(normalizeDate(estimate?.expiresAt)),
  }

  let docInactive: DocToHeader = {
    state: DocStateSpecial.Activo,
    date: '',
    cause: '',
    by: '',
  }

  if (isAnulada) {
    docInactive = {
      state: DocStateSpecial.Nulo,
      date: estimate?.voidedAt ?? '',
      cause: estimate.voidedCause ?? '',
      by: estimate?.voidedBy_user ?? '',
    }
  } else if (isExpirada) {
    docInactive = {
      state: DocStateSpecial.Expirado,
      date: estimate?.expiresAt ?? '',
      cause: '',
      by: '',
    }
  }

  const clientUser: ClientUser = {
    client: {
      firstname: estimate?.client.name ?? '',
      lastname: '',
      nin: estimate?.client.nin ?? '',
    },
  }

  const footer = CompleteMark(estimate?.footer ?? '', clientUser)

  return (
    <>
      <DocStatusHeader docName="cotización" state={docInactive} />
      <DocHeader header={docHeader} />
      <OrganizationFrame organization={organization} />

      <hr className="w-full border-t border-black-500 my-2" />
      <ClientFrame client={clientHeader} />
      <hr className="w-full border-t border-black-500 my-2" />
      <VehicleFrame vehicle={vehicleHeader} />
      <hr className="w-full border-t border-black-500 my-2" />
      <div className="mb-2">
        <span className="font-bold">Precio de Venta: {salePrice_format}</span>
      </div>
      {!estimate?.discountHide && (
        <div className="mb-2">
          <span>Descuento: {discount_format}%</span>
        </div>
      )}
      <hr className="w-full border-t border-black-500 my-2" />

      <DocNotes notes={estimate?.notes} />
      <hr className="w-full border-t border-black-500 my-2" />
      <div className="mb-0">{footerExpira}</div>
      <Remark>{footer}</Remark>
    </>
  )
}

export default EstimateDetail
